import { useContext, useState } from 'react'

import { Box, Button, Icon, Link, SelectOption, Text } from '@factorialco/deprecated-design-system'
import { EnvironmentContext } from 'config/environment'
import {
  FileSelectorModalDocument,
  FileSelectorModalResultsDocument,
  PayrollPolicyFragment,
  PayrollPolicyPeriodFragment,
  PayrollSummaryImportTemplateFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import DropZone from 'modules/core/components/DropZone'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/Select'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { PayrollPayrollSummaryImport } from 'modules/payroll_bundle/components/PayrollSummaryImport/PayrollSummaryImportModal/ImportSummaryWizard/types'
import ResultLine from 'modules/payroll_bundle/components/common/components/ResultLine'
import useCreatePayrollSummaryImport, {
  CreatePayrollSummaryImportResult,
} from 'modules/payroll_bundle/hooks/useCreatePayrollSummaryImport'
import {
  RESULT_ACCEPTED_MIME_TYPES,
  ResultConcepts,
  type ResultConceptsType,
  resultConceptsLabels,
} from 'modules/payroll_bundle/lib/constants'

const acceptedDocuments = RESULT_ACCEPTED_MIME_TYPES.reduce((acc, acceptedMimeType) => {
  return { ...acc, [acceptedMimeType]: [] }
}, {})

interface Props {
  companyId: number
  payrollPolicies: PayrollPolicyFragment[]
  initialPolicyId?: number
  initialPolicyPeriodId?: number
  loading?: boolean
  initialPayrollResultDocumentId?: number
  initialPayrollResultDocumentFilename?: string
  source: string
  title?: string
  onFinish?: (
    payrollSummaryImport: PayrollPayrollSummaryImport,
    payrollPolicy?: PayrollPolicyFragment,
    payrollPolicyPeriod?: PayrollPolicyPeriodFragment
  ) => void
  onClose?: () => void
}

const FileSelectorModal = ({
  companyId,
  loading,
  initialPolicyId,
  initialPolicyPeriodId,
  payrollPolicies,
  initialPayrollResultDocumentId,
  initialPayrollResultDocumentFilename = '',
  source,
  title,
  onFinish,
  onClose,
}: Props) => {
  const env = useContext(EnvironmentContext)
  const backendUrl = env.apiEndpoint
  const [uploadedFile, setUploadedFile] = useState<(File | null | undefined) | null>(null)
  const [importOngoing, setImportOngoing] = useState<boolean>(false)
  const [importData, setImportData] = useState<CreatePayrollSummaryImportResult>(null)
  const [_importError, setImportError] = useState<unknown | null | undefined>(null)
  const [hasPreviousResults, setHasPreviousResults] = useState<boolean>(false)
  const [imporModalDocument] = useLazyQuery(FileSelectorModalResultsDocument)
  const { createPayrollSummaryImport } = useCreatePayrollSummaryImport()
  const [compensationType, setCompensationType] = useState<ResultConcepts>(
    ResultConcepts.regular_payroll
  )
  const [templateLink, setTemplateLink] = useState<string | null>(null)
  const [loadingInfo, setLoadingInfo] = useState<boolean>(false)
  const [policyPeriods, setPolicyPeriods] = useState<PayrollPolicyPeriodFragment[]>([])
  const [selectedPolicy, setSelectedPolicy] = useState<PayrollPolicyFragment | undefined>()
  const [previousResultCreatedAt, setPreviousResultCreatedAt] = useState<string | null>(null)
  const [previousResultFilename, setPreviousResultFilename] = useState<string | null>(null)
  const [selectedPolicyPeriod, setSelectedPolicyPeriod] = useState<
    PayrollPolicyPeriodFragment | undefined
  >()
  const [templates, setTemplates] = useState<PayrollSummaryImportTemplateFragment[]>([])
  const hasPreviousPayrollResultDocument = initialPayrollResultDocumentId !== undefined

  const [fetch] = useLazyQuery(FileSelectorModalDocument, { fetchPolicy: 'no-cache' })

  useUnsafeEffect(() => {
    if (payrollPolicies) {
      setSelectedPolicy(
        payrollPolicies.find((policy) => policy.id === initialPolicyId) || payrollPolicies[0]
      )
    }
  }, [payrollPolicies, initialPolicyId, initialPolicyPeriodId])

  useUnsafeEffect(() => {
    async function fetchDataModal() {
      if (selectedPolicy) {
        setSelectedPolicyPeriod(undefined)
        const { data } = await fetch({
          variables: {
            policyId: selectedPolicy.id,
            sortOrder: { field: 'startsOn', order: 'desc' },
          },
        })

        if (data) {
          setPolicyPeriods(data?.payroll.policyPeriodsConnection.nodes)
          if (!templates.length) {
            setTemplates(data?.payroll.payrollSummaryImportTemplatesConnection.nodes)
          }
        }
      }
    }
    fetchDataModal()
    generateTemplateLink()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPolicy])

  useUnsafeEffect(() => {
    if (policyPeriods) {
      setSelectedPolicyPeriod(
        policyPeriods.find((period) => period.id === initialPolicyPeriodId) || policyPeriods[0]
      )
    }
    generateTemplateLink()
  }, [policyPeriods, initialPolicyPeriodId])

  useUnsafeEffect(() => {
    async function fetchData() {
      if (selectedPolicyPeriod) {
        setLoadingInfo(true)
        const { data: importModalData } = await imporModalDocument({
          variables: {
            policyPeriodId: selectedPolicyPeriod?.id,
            sortOrder: { field: 'createdAt', order: 'desc' },
          },
        })

        const previousUploadedResults = importModalData?.payroll?.resultsConnection?.nodes ?? []

        const previousResult = previousUploadedResults?.find(
          (result) => result.concept === compensationType
        )

        setHasPreviousResults(previousResult !== undefined)

        if (previousResult) {
          setPreviousResultCreatedAt(
            dateToString(moment(previousResult.createdAt), 'ui_short_with_time')
          )
          setPreviousResultFilename(previousResult.filename)
        }

        setLoadingInfo(false)
      }
    }
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPolicyPeriod, compensationType])

  const handleImportFile = async () => {
    if (!uploadedFile || !selectedPolicyPeriod) return

    const result = await createPayrollSummaryImport({
      companyId: companyId,
      compensationType: compensationType,
      file: uploadedFile,
      policyPeriodId: selectedPolicyPeriod.id,
    })

    return handleResult(result, () => undefined)
  }

  const handlePayrollResultDocument = async () => {
    if (!initialPayrollResultDocumentId || !selectedPolicyPeriod) return

    const result = await createPayrollSummaryImport({
      companyId: companyId,
      compensationType: compensationType,
      policyPeriodId: selectedPolicyPeriod.id,
      documentId: initialPayrollResultDocumentId,
    })

    return handleResult(result, () => undefined)
  }

  const conceptOptions: SelectOption[] = Object.keys(resultConceptsLabels()).map((key) => ({
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    label: resultConceptsLabels()[key as ResultConceptsType] ?? '',
    value: key,
  }))

  const policyOptions: SelectOption[] = payrollPolicies.map((policy) => ({
    label: policy.name || policy.id.toString(),
    value: policy.id.toString(),
  }))

  const periodOptions: SelectOption[] = policyPeriods.map((period) => ({
    label: period.name || period.id.toString(),
    value: period.id.toString(),
  }))

  const onDropFile = async (files: File[]) => {
    const file = files[0]

    if (!file) return

    setUploadedFile(file)
    track(trackingEvents.payroll.selectResultFile, {
      period_status: selectedPolicyPeriod?.status,
      source,
    })
  }

  const importFile = async () => {
    if (!isFileReady() || !selectedPolicyPeriod) return

    let handledImportFile = undefined

    if (!hasPreviousPayrollResultDocument) {
      handledImportFile = await handleImportFile()
    } else {
      handledImportFile = await handlePayrollResultDocument()
    }

    if (!handledImportFile?.success || !handledImportFile.data.payrollSummaryImport) {
      if (!handledImportFile?.success) setImportError(handledImportFile?.errors)
      return
    }

    setImportData(handledImportFile.data.payrollSummaryImport)

    setImportOngoing(true)

    track(trackingEvents.payroll.importSelectedResultFile, {
      period_status: selectedPolicyPeriod.status,
      source,
    })
  }

  const isFileReady = () => {
    return (!hasPreviousPayrollResultDocument && uploadedFile) || hasPreviousPayrollResultDocument
  }

  const generateTemplateLink = () => {
    if (templates.length) {
      const template = templates.find((temp) => temp.id === selectedPolicy?.country)

      if (template) {
        setTemplateLink(`${backendUrl}/${template?.url}`)
      } else {
        setTemplateLink(null)
      }
    }
  }

  useUnsafeEffect(() => {
    if (importData) {
      onFinish &&
        onFinish(
          // eslint-disable-next-line no-type-assertion/no-type-assertion
          { ...importData, compensationType: compensationType } as PayrollPayrollSummaryImport,
          selectedPolicy,
          selectedPolicyPeriod
        )
    }
  }, [importData, onFinish])

  const handleTypeOfPaySelectChange = (value: string) => {
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    setCompensationType(value as ResultConcepts)
  }

  const handlePolicySelectChange = (value: string) => {
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    const policy = payrollPolicies.find((policy) => policy.id === parseInt(value))
    setSelectedPolicy(policy)
  }

  const handlePeriodSelectChange = (value: string) => {
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    const period = policyPeriods.find((period) => period.id === parseInt(value))
    setSelectedPolicyPeriod(period)
  }

  if (loading) return <Loading />

  let titleText = ''
  let descriptionText = ''
  let buttonText = ''

  if (!hasPreviousPayrollResultDocument) {
    titleText = title || i18n.t('payroll_bundle.payroll_summary_import.modal.file_selector.title')
    descriptionText = i18n.t(
      'payroll_bundle.payroll_summary_import.modal.file_selector.description'
    )
    buttonText = i18n.t('payroll_bundle.payroll_summary_import.modal.file_selector.import_file')
  } else {
    titleText =
      title ||
      i18n.t('payroll_bundle.payroll_summary_import.modal.file_selector.withResultDocument.title')
    descriptionText = i18n.t(
      'payroll_bundle.payroll_summary_import.modal.file_selector.withResultDocument.description',
      { file: initialPayrollResultDocumentFilename }
    )
    buttonText = i18n.t(
      'payroll_bundle.payroll_summary_import.modal.file_selector.withResultDocument.import_file'
    )
  }

  const trackDownloadTemplate = () => {
    track(trackingEvents.payroll.downloadTemplate, {
      country: selectedPolicy?.country,
      company_id: companyId,
      source,
    })
  }

  return (
    <Modal
      title={titleText}
      description={descriptionText}
      illustration="file-upload"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          <Button
            onClick={importFile}
            type="primary"
            disabled={
              (!uploadedFile && !hasPreviousPayrollResultDocument) || importOngoing || loadingInfo
            }
            arial-label={'importS'}
          >
            {buttonText}
          </Button>
        </>
      }
    >
      <Box gap="s16">
        <>
          <Box flexDirection="row" alignItems="flexEnd" gap="s32">
            <Box width="s400">
              <Select
                label={i18n.t(
                  'payroll_bundle.payroll_summary_import.modal.file_selector.type_of_pay'
                )}
                options={conceptOptions}
                onChange={handleTypeOfPaySelectChange}
              />
            </Box>
          </Box>
          {!hasPreviousPayrollResultDocument &&
            payrollPolicies.length > 1 &&
            !initialPolicyPeriodId && (
              <Box flexDirection="row" alignItems="flexEnd" gap="s32">
                <Box width="s400">
                  <Select
                    label={i18n.t(
                      'payroll_bundle.payroll_summary_import.modal.file_selector.policy'
                    )}
                    options={policyOptions}
                    onChange={handlePolicySelectChange}
                  />
                </Box>
              </Box>
            )}
          {!hasPreviousPayrollResultDocument && !initialPolicyPeriodId && (
            <Box flexDirection="row" alignItems="flexEnd" gap="s32">
              <Box width="s400">
                <Select
                  label={i18n.t('payroll_bundle.payroll_summary_import.modal.file_selector.period')}
                  options={periodOptions}
                  onChange={handlePeriodSelectChange}
                />
              </Box>
            </Box>
          )}
          {hasPreviousResults && (
            <Box padding="s24" background="tangerine200" borderRadius={{ all: 'abs016' }} gap="s8">
              <Box flexDirection="row" alignItems="center" gap="s4">
                <Icon.Medium icon="alert-sign" color="tangerine1000" />
                <Text.Normal size="300" weight="semibold" color="grey1000">
                  {i18n.t(
                    'payroll_bundle.payroll_summary_import.modal.file_selector.has_previous_import.title'
                  )}
                </Text.Normal>
              </Box>
              <Text.Normal size="300" color="grey1000">
                {i18n.t(
                  'payroll_bundle.payroll_summary_import.modal.file_selector.has_previous_import.description'
                )}
              </Text.Normal>
              {previousResultCreatedAt !== null && (
                <Box flexDirection="row" alignItems="center" justifyContent="flexStart">
                  <Text.Normal size="200">
                    {` ${previousResultCreatedAt} - ${previousResultFilename}`}
                  </Text.Normal>
                </Box>
              )}
            </Box>
          )}
        </>
        {!hasPreviousPayrollResultDocument && (
          <Box gap="s4" flexGrow>
            <Text.Normal size="200" weight="medium" color="grey1000">
              {i18n.t('payroll_bundle.payroll_summary_import.modal.file_selector.payroll_data')}
            </Text.Normal>
            <DropZone
              accept={acceptedDocuments}
              onDrop={onDropFile}
              multiple={false}
              placeholder={i18n.t(
                'payroll_bundle.payroll_summary_import.modal.file_selector.dropzone_placeholder'
              )}
            />
            <Box paddingTop="s8">
              {uploadedFile && (
                <ResultLine filename={uploadedFile.name} onRemove={() => setUploadedFile(null)} />
              )}
            </Box>
            {templateLink && (
              <Box flexDirection="row" alignItems="center" gap="s4">
                <Text.Normal size="200" weight="medium" color="grey1000">
                  {i18n.t(
                    'payroll_bundle.payroll_summary_import.modal.file_selector.template_title'
                  )}
                </Text.Normal>
                <Link.Href href={templateLink} onClick={trackDownloadTemplate}>
                  {i18n.t(
                    'payroll_bundle.payroll_summary_import.modal.file_selector.template_link'
                  )}
                </Link.Href>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Modal>
  )
}

export default FileSelectorModal
